import { useEffect, useState } from "react";
import { Problem } from "../api/backendApi";
import ErrorDisplay from "../common/ErrorDisplay";
import LoadingDisplay from "../common/LoadingDisplay";
import { Button, Card, Container, Form, InputGroup } from "react-bootstrap";
import { useRoutablePages } from "../common/RoutablePages";
import { MakeModel } from "../api/makeAPI";
import { Search } from "react-bootstrap-icons";
import {
  YearlyOverviewVehicleFileViewModel,
  yearlyReviewApi,
} from "../api/yearlyReviewApi";
import { useAuth } from "react-oidc-context";
import { useLocation, useNavigate } from "react-router-dom";

function YearlyOverviewVehicleFilePage() {
  const location = useLocation();
  const [yearlyOverviewVehicleFiles, setYearlyOverviewVehicleFiles] = useState<
    YearlyOverviewVehicleFileViewModel[]
  >([]);
  const [
    filteredYearlyOverviewVehicleFiles,
    setFilteredYearlyOverviewVehicleFiles,
  ] = useState<YearlyOverviewVehicleFileViewModel[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [yearFilter, setYearFilter] = useState<number[]>([]);
  const [selectedYear, setSelectedYear] = useState<number>(
    location.state !== null && location.state.yearEnd !== null
      ? location.state.yearEnd
      : new Date().getFullYear(),
  );
  const [selectedMakeCode, setSelectedMakeCode] = useState<string>();
  const [makeFilter, setMakesFilter] = useState<MakeModel[]>([]);
  const [error, setError] = useState<Problem | undefined>(undefined);
  const routes = useRoutablePages();
  const auth = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    async function loadYearlyOverviewVehicleFiles() {
      setLoading(true);
      const data = await yearlyReviewApi.getYearlyOverviewVehicleFiles();

      if (data.ok) {
        setError(undefined);
        setYearlyOverviewVehicleFiles(data.value);
        var uniqueYears = Array.from(
          new Set(data.value.flatMap((vehicle) => vehicle.endYearsForVehicles)),
        ).sort((a, b) => a - b);
        setYearFilter(uniqueYears);

        var distinctMakes = await loadDistinctMakes(data.value);
        updateListAfterReview(distinctMakes);
      } else {
        setError(data.problem);
      }
      setLoading(false);
    }
    loadYearlyOverviewVehicleFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDistinctMakes = async (
    yearlyOverviewVehicleFiles: YearlyOverviewVehicleFileViewModel[],
  ) => {
    const distinctMakes = new Map();

    yearlyOverviewVehicleFiles.forEach((vehicle) => {
      if (vehicle.endYearsForVehicles.includes(selectedYear)) {
        const makeCode = vehicle.make.code;

        if (!distinctMakes.has(makeCode)) {
          distinctMakes.set(makeCode, vehicle.make);
        }
      }
    });

    setMakesFilter(Array.from(distinctMakes.values()));

    return Array.from(distinctMakes.values());
  };

  const sortYearlyOverviewVehicleFiles = () => {
    var filteredVehicleFiles = [...yearlyOverviewVehicleFiles];
    filteredVehicleFiles = filteredVehicleFiles.filter((vehicleFile) =>
      selectedMakeCode
        ? vehicleFile.endYearsForVehicles.includes(selectedYear) &&
          vehicleFile.make.code === selectedMakeCode
        : vehicleFile.endYearsForVehicles.includes(selectedYear),
    );
    setFilteredYearlyOverviewVehicleFiles(filteredVehicleFiles);
  };

  useEffect(() => {
    loadDistinctMakes(yearlyOverviewVehicleFiles);
    sortYearlyOverviewVehicleFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, selectedMakeCode, yearlyOverviewVehicleFiles]);

  function updateListAfterReview(distinctMakes: any) {
    if (location.state !== null && location.state.makeCode !== null) {
      if (distinctMakes.some((x: any) => x.code === location.state.makeCode)) {
        setSelectedMakeCode(location.state.makeCode);
        navigate(location, {
          state: { makeCode: null, yearEnd: location.state.yearEnd },
          replace: false,
        });
      }
    }
  }

  return (
    <>
      <div className="main-container-title">
        <h1>Yearly Overview</h1>
        <ErrorDisplay error={error} />
        <div style={{ display: "flex", flexDirection: "row" }}>
          {!loading && (
            <InputGroup
              className="sm mb-2 mt-1"
              style={{
                display: "flex",
                width: "fit-content",
                marginRight: "1rem",
              }}
            >
              <span
                className="input-group-text"
                style={{ display: "flex", flexDirection: "row" }}
              >
                <Search width={12} height={12} />
                <Form.Label
                  style={{
                    fontWeight: "400",
                    fontSize: "16px",
                    display: "flex",
                    marginTop: "auto",
                    marginBottom: "auto",
                    marginLeft: "5px",
                  }}
                >
                  year
                </Form.Label>
              </span>
              <Form.Select
                id="toolbarYears"
                value={selectedYear}
                onChange={(e) => {
                  setSelectedYear(parseInt(e.target.value));
                  setSelectedMakeCode("");
                }}
              >
                {!selectedYear && (
                  <option key="" value="">
                    [Select year]
                  </option>
                )}
                {yearFilter.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </Form.Select>
            </InputGroup>
          )}
          {!loading && (
            <InputGroup
              className="sm mb-2 mt-1"
              style={{
                display: "flex",
                width: "fit-content",
                marginRight: "1rem",
              }}
            >
              <span
                className="input-group-text"
                style={{ display: "flex", flexDirection: "row" }}
              >
                <Search width={12} height={12} />
                <Form.Label
                  style={{
                    fontWeight: "400",
                    fontSize: "16px",
                    display: "flex",
                    marginTop: "auto",
                    marginBottom: "auto",
                    marginLeft: "5px",
                  }}
                >
                  make
                </Form.Label>
              </span>
              <Form.Select
                id="toolbarYears"
                value={selectedMakeCode}
                onChange={(e) => {
                  setSelectedMakeCode(e.target.value);
                }}
              >
                <option key="" value="">
                  [Select make]
                </option>
                {makeFilter.map((m) => (
                  <option key={m.code} value={m.code}>
                    {m.name} [{m.code}]
                  </option>
                ))}
              </Form.Select>
            </InputGroup>
          )}
          <Button
            disabled={
              selectedYear &&
              selectedMakeCode &&
              filteredYearlyOverviewVehicleFiles.some(
                (x) =>
                  x.checkedOutBy === auth.user?.profile.name ||
                  x.checkedOutBy === null,
              )
                ? false
                : true
            }
            className="mt-1"
            variant="success"
            style={{
              display: "flex",
              height: "fit-content",
              marginLeft: "5px",
            }}
            onClick={() =>
              routes.toModelInfoToReview(selectedMakeCode, selectedYear)
            }
          >
            Start review
          </Button>
        </div>
      </div>
      <div className="main-container-content">
        <LoadingDisplay loading={loading} />
        {!loading && (
          <Container style={{ fontSize: "80%" }} className="overflow-auto">
            {filteredYearlyOverviewVehicleFiles.map((vf) => (
              <Card key={vf.number} style={{ marginBottom: "5px" }}>
                <Card.Header>
                  <div style={{ display: "flex", flexDirection: "row" }}>
                    <div
                      className="h5"
                      style={{ display: "flex", flexDirection: "column" }}
                    >
                      {vf.number}
                    </div>
                    <div
                      className="h5"
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        marginLeft: "auto",
                      }}
                    >
                      {vf.endYearsForVehicles.join("/")}
                    </div>
                  </div>
                </Card.Header>
                <Card.Body></Card.Body>
                {vf.checkedOutBy && (
                  <Card.Footer>
                    <div style={{ display: "flex", flexDirection: "row" }}>
                      <small>Checked out by {vf.checkedOutBy}</small>
                    </div>
                  </Card.Footer>
                )}
              </Card>
            ))}
          </Container>
        )}
      </div>
    </>
  );
}

export default YearlyOverviewVehicleFilePage;
